import { deserializeHotspot, deserializeElement } from './helpers';

export const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// ── Initial state ─────────────────────────────────────────────────────────────
export const INITIAL_STATE = {
  status:              'idle',
  lobby:               null,
  event:               null,
  siblings:            [],
  hotspots:            [],
  error:               null,
  history:             [],
  introVideoUrl:       null,
  pendingTransitionUrl: null,
  loadProgress:        0,
};

// ── Reducer ───────────────────────────────────────────────────────────────────
export function reducer(state, action) {
  switch (action.type) {
    case 'LOAD_START':
      return { ...state, status: 'loading', loadProgress: 0, error: null };

    case 'PRELOAD_START':
      return { ...state, status: 'preloading', loadProgress: 0 };

    case 'PRELOAD_PROGRESS':
      return { ...state, loadProgress: action.payload };

    case 'LOAD_SUCCESS': {
      const { lobby: rawLobby, event, siblings, pushHistory } = action.payload;
      const hotspots = [
        ...(rawLobby.hotspots || []).map(deserializeHotspot),
        ...(rawLobby.elements || []).map(deserializeElement),
      ].filter((h, i, arr) => arr.findIndex(x => x.id === h.id) === i);

      // Deserialize hotspots and elements inside carousel slides so the engine
      // receives the same hotspotType/hotspotAction shape it expects.
      const slides = (rawLobby.slides || []).map(slide => ({
        ...slide,
        hotspots: (slide.hotspots || []).map(deserializeHotspot),
        elements: (slide.elements || []).map(deserializeElement),
      }));
      const lobby = { ...rawLobby, slides };

      const isFirstLoad = !pushHistory && (state.history || []).length === 0;
      const history     = pushHistory
        ? [...(state.history || []), state.lobby?._id].filter(Boolean)
        : (state.history || []);
      const introVideo  = isFirstLoad ? (lobby.introVideoUrl || null) : null;
      const status      = introVideo ? 'intro' : 'visible';

      return {
        ...state,
        status, lobby, event, siblings, hotspots, error: null, history,
        introVideoUrl: introVideo,
      };
    }

    case 'INTRO_DONE':
      return { ...state, status: 'visible', introVideoUrl: null };

    case 'TRANSITION_DONE':
      return { ...state, status: 'visible' };

    case 'LOAD_ERROR':
      return { ...state, status: 'error', error: action.payload };

    case 'GO_BACK': {
      const history = [...(state.history || [])];
      history.pop();
      return { ...state, history, status: 'loading', error: null };
    }

    default:
      return state;
  }
}

// ── API ───────────────────────────────────────────────────────────────────────
export async function fetchLobby(lobbyId) {
  const res = await fetch(`${API_BASE}/lobby-engine/${lobbyId}`);
  if (!res.ok) throw new Error(`Failed to load lobby (${res.status})`);
  const json = await res.json();
  if (!json.success) throw new Error(json.message || 'Unknown error');
  return json.data;
}

// ── Asset preloaders ──────────────────────────────────────────────────────────
export function preloadImage(url) {
  if (!url) return Promise.resolve();
  return new Promise(resolve => {
    const img   = new Image();
    img.onload  = resolve;
    img.onerror = resolve;
    img.src     = url;
  });
}

export function preloadVideo(url) {
  if (!url) return Promise.resolve();
  return new Promise(resolve => {
    const video        = document.createElement('video');
    video.preload      = 'metadata';
    video.muted        = true;
    video.crossOrigin  = 'anonymous';
    const t            = setTimeout(resolve, 4000);
    video.onloadedmetadata = () => { clearTimeout(t); resolve(); };
    video.oncanplay        = () => { clearTimeout(t); resolve(); };
    video.onerror          = () => { clearTimeout(t); resolve(); };
    video.src              = url;
  });
}

function getLobbyAssets(lobbyData) {
  const assets = [];
  const lobby  = lobbyData.lobby;
  if (lobby.bgUrl) assets.push({ url: lobby.bgUrl, type: lobby.bgType === 'video' ? 'video' : 'image' });
  if (lobby.introVideoUrl) assets.push({ url: lobby.introVideoUrl, type: 'video' });
  return assets;
}

export async function preloadLobbyAssets(lobbyData, onProgress) {
  const assets = getLobbyAssets(lobbyData);
  if (!assets.length) { onProgress(1); return; }
  let done = 0;
  await Promise.all(assets.map(a => {
    const p = a.type === 'video' ? preloadVideo(a.url) : preloadImage(a.url);
    return p.then(() => { done++; onProgress(done / assets.length); });
  }));
}